import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, get, post, put } from '../api.js';
import { PageTitle } from '../frame.js';
import { t } from '../i18n.js';
import { Link } from '../router.js';
import { useSession } from '../session.js';
import { fmtDateTime } from '../util.js';

interface AiSettingsDto {
  settings: { provider: 'disabled' | 'anthropic'; model: string; effort: 'low' | 'medium' | 'high'; hasKey: boolean; keyHint: string | null; updatedAt: string | null };
  source: 'settings' | 'env';
  error: string | null;
  canStoreKey: boolean;
  env: { provider: string; hasKey: boolean; model: string };
  models: Array<{ id: string; name: string }>;
  status: { enabled: boolean; model: string; provider: string };
}
interface TestResult {
  ok: boolean;
  model?: string;
  ms?: number;
  titles?: string[];
}
const errText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

/** Διαχείριση → Βοηθός AI: which provider and model the newsroom uses, and the API key, kept sealed on the server. */
export function AdminAiPage() {
  const { can } = useSession();
  const [data, setData] = useState<AiSettingsDto | null>(null);
  const [provider, setProvider] = useState<'disabled' | 'anthropic'>('disabled');
  const [model, setModel] = useState('claude-opus-5-5');
  const [effort, setEffort] = useState<'low' | 'medium' | 'high'>('medium');
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [test, setTest] = useState<TestResult | null>(null);

  const apply = (d: AiSettingsDto) => {
    setData(d);
    setProvider(d.settings.provider);
    setModel(d.settings.model);
    setEffort(d.settings.effort);
  };
  useEffect(() => {
    get<AiSettingsDto>('/api/admin/ai-settings')
      .then(apply)
      .catch((e) => setError(errText(e)));
  }, []);

  if (!can('admin.config')) return <div className="banner err">{t('noPermission')}</div>;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy('save');
    setError(null);
    setNotice(null);
    setTest(null);
    try {
      const d = await put<AiSettingsDto>('/api/admin/ai-settings', { provider, model, effort });
      apply(d);
      setNotice(d.status.enabled ? `${t('aiSettingsSaved')} · ${t('aiEnabledWith')} ${d.status.model}` : `${t('aiSettingsSaved')} · ${t('aiStillOff')}${d.error ? ` (${d.error})` : ''}`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };
  const runTest = async () => {
    setBusy('test');
    setError(null);
    setTest(null);
    try {
      setTest(await post<TestResult>('/api/admin/ai-settings/test'));
    } catch (err) {
      setTest({ ok: false });
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageTitle title={t('aiAdminTitle')} hint={t('aiAdminHint')} />
      {error && <div className="banner err">{error}</div>}
      {notice && <div className="banner info">{notice}</div>}
      {data && !data.canStoreKey && <div className="banner warn">{t('aiNoSecret')}</div>}
      {data?.error && <div className="banner warn">{data.error}</div>}
      {data && (
        <p className="tip">
          {t('aiCurrent')}: {data.status.enabled ? <span className="status ok">{t('active')}</span> : <span className="status warn">{t('inactive')}</span>} {data.status.enabled ? `· ${data.status.model}` : ''} · {t('aiSource')}: {data.source === 'settings' ? t('aiSourceSettings') : t('aiSourceEnv')}
          {data.settings.updatedAt ? ` · ${t('updated')}: ${fmtDateTime(data.settings.updatedAt)}` : ''}
        </p>
      )}
      <form onSubmit={save} className="card" style={{ maxWidth: 720 }}>
        <div className="form-grid" style={{ gridTemplateColumns: 'max-content 1fr' }}>
          <label htmlFor="ai-prov">{t('aiProvider')} :</label>
          <select id="ai-prov" value={provider} onChange={(e) => setProvider(e.target.value as 'disabled' | 'anthropic')}>
            <option value="disabled">{t('aiProviderOff')}</option>
            <option value="anthropic">Anthropic (Claude)</option>
          </select>
          <label htmlFor="ai-model">{t('aiModel')} :</label>
          <select id="ai-model" value={model} onChange={(e) => setModel(e.target.value)} disabled={provider === 'disabled'}>
            {(data?.models ?? [{ id: model, name: model }]).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <label htmlFor="ai-effort">{t('aiEffort')} :</label>
          <select id="ai-effort" value={effort} onChange={(e) => setEffort(e.target.value as 'low' | 'medium' | 'high')} disabled={provider === 'disabled'}>
            <option value="low">{t('aiEffortLow')}</option>
            <option value="medium">{t('aiEffortMedium')}</option>
            <option value="high">{t('aiEffortHigh')}</option>
          </select>
          <label>{t('aiKey')} :</label>
          <div>
            {data?.settings.hasKey ? (
              <span className="status ok">
                {t('aiKeyStored')} …{data.settings.keyHint ?? ''}
              </span>
            ) : data?.env.hasKey ? (
              <span className="status info">{t('keyFromEnv')}</span>
            ) : (
              <span className="status warn">{t('aiKeyMissing')}</span>
            )}{' '}
            <span className="small muted">
              {t('aiKeyWhere')} <Link to="/admin/keys">{t('keysMenu')} ›</Link>
            </span>
          </div>
          <span />
          <div className="toolbar left">
            <button className="btn primary" disabled={busy !== null}>
              💾 {t('save')}
            </button>
            <button type="button" className="btn" disabled={busy !== null || !data?.status.enabled} onClick={() => void runTest()}>
              ⚡ {t('aiTest')}
            </button>
            {busy === 'test' && <span className="muted">{t('aiTesting')}</span>}
          </div>
        </div>
      </form>
      {test && (
        <div className={`banner ${test.ok ? 'info' : 'err'}`}>
          {test.ok ? (
            <span>
              {t('aiTestOk')} · {test.model} · {test.ms} ms · {(test.titles ?? []).slice(0, 2).join(' / ')}
            </span>
          ) : (
            <span>{t('aiTestFailed')}</span>
          )}
        </div>
      )}
      <p className="tip">{t('aiAdminTip')}</p>
    </>
  );
}
